'use client';

import { c as _c } from "react/compiler-runtime";
import { useSyncExternalStore } from 'react';
import { type FeatureGateName, featureGates } from '@/config/feature-gates';
import { checkGate, getProviderReady, subscribeProviderReady } from '@/lib/analytics';
import { getGateValuesMissing, getGateValuesPending, getGateValuesVersion, getServedGateValue, rememberServedGateValue, subscribeGateValues } from '@/lib/analytics/gate-values';
import { getDevToolkitAccess, subscribeDevToolkitAccess, useDevToolkitStore } from '@/stores/dev-toolkit';

/**
 * Reads a feature gate for rendering and re-renders when its value can change.
 *
 * @param name - The gate's key in `featureGates`.
 * @returns The gate value, or `null` while it is not decidable: the provider is
 * still loading; an identity update to a different user is in flight for a
 * user-scoped gate, so the current evaluation may be the previous user's; or an
 * identity update has no cached values to serve yet (a first sign-in on this
 * device) and the gate has not served a value that applies.
 *
 * @remarks
 * Callers must preserve pending actions such as deep-link parameters until the
 * value becomes boolean. A DevToolkit override wins while toolkit access is
 * granted. While an identity update has no values to serve, the gate keeps the
 * value it last served, so a page that shows it does not drop to loading and
 * remount; a user-scoped gate keeps only a value served for the same user. The
 * gate is read again when the provider becomes ready, when the last identity
 * update settles, and when the override changes. A stable-device gate does not
 * wait for identity updates, and re-identifying the same user does not turn the
 * value into `null`.
 *
 * @example
 * ```tsx
 * // `name` is a key of `featureGates`, such as 'gifting'.
 * const enabled = useFeatureGate(name);
 * if (enabled === null) return <PageSkeleton />;
 * ```
 */
export function useFeatureGate(name) {
  const $ = _c(10);
  let t0;
  if ($[0] !== name) {
    t0 = state => state.overrides[name];
    $[0] = name;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const localOverride = useDevToolkitStore(t0);
  const devToolkitAccess = useSyncExternalStore(subscribeDevToolkitAccess, getDevToolkitAccess, _temp);
  const providerReady = useSyncExternalStore(subscribeProviderReady, getProviderReady, _temp2);
  const gateValuesVersion = useSyncExternalStore(subscribeGateValues, getGateValuesVersion, _temp3);
  const identityPending = useSyncExternalStore(subscribeGateValues, getGateValuesPending, _temp4);
  const valuesMissing = useSyncExternalStore(subscribeGateValues, getGateValuesMissing, _temp5);
  const userScoped = featureGates[name].idType !== "stableID";
  const t1 = devToolkitAccess ? localOverride : undefined;
  const t2 = identityPending && userScoped;
  let t3;
  if ($[2] !== gateValuesVersion || $[3] !== name || $[4] !== providerReady || $[5] !== t1 || $[6] !== t2 || $[7] !== userScoped || $[8] !== valuesMissing) {
    t3 = readGate(name, {
      override: t1,
      providerReady,
      userScoped,
      awaitingIdentity: t2,
      valuesMissing,
      gateValuesVersion
    });
    $[2] = gateValuesVersion;
    $[3] = name;
    $[4] = providerReady;
    $[5] = t1;
    $[6] = t2;
    $[7] = userScoped;
    $[8] = valuesMissing;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  return t3;
}

/**
 * The runtime inputs of a gate read. React Compiler caches the `readGate` call
 * on them, and `checkGate` reads mutable provider state, so they change when
 * the provider's answer can. `gateValuesVersion` is not read: it bumps when the
 * last identity update settles, which makes the compiler read again. A
 * provider reinstall is not an input: `clientKey` does not change while the
 * app runs.
 */
function _temp5() {
  return false;
}
function _temp4() {
  return false;
}
function _temp3() {
  return 0;
}
function _temp2() {
  return false;
}
function _temp() {
  return false;
}
interface GateInputs {
  /** The DevToolkit override, only while toolkit access is granted. */
  override: boolean | undefined;
  providerReady: boolean;
  /** The gate is evaluated per user, not per device. */
  userScoped: boolean;
  /** A user-scoped gate during an update to another user; never a stable-device gate. */
  awaitingIdentity: boolean;
  /** An identity update is in flight and the provider has no values to serve. */
  valuesMissing: boolean;
  gateValuesVersion: number;
}
function readGate(name: FeatureGateName, inputs: GateInputs): boolean | null {
  if (inputs.override !== undefined) return checkGate(name);
  if (!inputs.providerReady || inputs.awaitingIdentity) return null;
  // The store is empty until the update settles; keep what this gate served.
  if (inputs.valuesMissing) return getServedGateValue(name, inputs.userScoped) ?? null;
  const value = checkGate(name);
  rememberServedGateValue(name, value);
  return value;
}